import { ReactNode } from 'react';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { styled } from '@mui/system';

export type SnackbarType = 'success' | 'error' | 'warning';

export interface SnackbarFeedback {
  type: FEEDBACK_TYPE;
  isPersistent?: boolean;
}

interface SnackbarFeedbackMap {
  message: string;
  status: SnackbarType;
  customIcon?: ReactNode;
  hideDuration?: number;
  disableWindowBlurListener?: boolean;
}

export enum FEEDBACK_TYPE {
  LOGGED_OUT = 'loggedOut',
  GENERAL_ERROR = 'generalError',
  IDENTIFICATION_ERROR = 'identificationError',
  PIN_INCORRECT_FEEDBACK = 'incorrectPinCode',
  E_SEND_LINK_SUCCESS = 'eSendLinkSuccess',
  E_SEND_LINK_ERROR = 'eSendLinkError',
  TRANSACTION_ERROR = 'transactionError',
  PROCESS_PAYMENT_ERROR = 'processPaymentError',
  CHARGE_SUCCESS = 'chargeSuccess',
  PASSWORD_CHANGE_SUCCESS = 'passwordChangeSuccess',
}

enum FEEDBACK_MESSAGES {
  IDENTIFICATION_ERROR = "Identification couldn't be completed at this moment - please try again later",
  GENERAL_ERROR = "We couldn't process your request at this moment - please try again later",
  SECURITY_LOGOUT_REASONS = "For security reasons, you've been logged out - Click 'Start' to log in.",
  PIN_INCORRECT_FEEDBACK = 'The PIN code you entered is incorrect. Please try again',
  E_SEND_LINK_SUCCESS = 'link sent successfully!',
  E_SEND_LINK_ERROR = 'oops! something went wrong - please try again',
  TRANSACTION_ERROR = 'This transaction must be manually settled through the merchant dashboard.',
  PROCESS_PAYMENT_ERROR = `We couldn't process your payment at this moment - please try again later`,
  CHARGE_SUCCESS = 'Charge successfully created',
}

const StyledLock = styled(LockOutlinedIcon)({
  fontSize: 36,
  marginRight: 15,
});

const LOG_OUT_TIME_DURATION = 1000 * 60 * 60 * 3;

export const feedbackSnackbarMap: Record<FEEDBACK_TYPE, SnackbarFeedbackMap> = {
  identificationError: {
    message: FEEDBACK_MESSAGES.IDENTIFICATION_ERROR,
    status: 'error',
  },
  generalError: {
    message: FEEDBACK_MESSAGES.GENERAL_ERROR,
    status: 'error',
  },
  loggedOut: {
    message: FEEDBACK_MESSAGES.SECURITY_LOGOUT_REASONS,
    status: 'warning',
    hideDuration: LOG_OUT_TIME_DURATION,
    customIcon: <StyledLock />,
    disableWindowBlurListener: true,
  },
  incorrectPinCode: {
    message: FEEDBACK_MESSAGES.PIN_INCORRECT_FEEDBACK,
    status: 'error',
  },
  eSendLinkSuccess: {
    message: FEEDBACK_MESSAGES.E_SEND_LINK_SUCCESS,
    status: 'success',
  },
  eSendLinkError: {
    message: FEEDBACK_MESSAGES.E_SEND_LINK_ERROR,
    status: 'error',
  },
  transactionError: {
    message: FEEDBACK_MESSAGES.TRANSACTION_ERROR,
    status: 'error',
  },
  processPaymentError: {
    message: FEEDBACK_MESSAGES.PROCESS_PAYMENT_ERROR,
    status: 'error',
  },
  chargeSuccess: {
    message: FEEDBACK_MESSAGES.CHARGE_SUCCESS,
    status: 'success',
  },
  passwordChangeSuccess: {
    message: 'Password changed successfully!',
    status: 'success',
  },
};

export const setFeedbackSession = (feedbackType: FEEDBACK_TYPE) => {
  sessionStorage.setItem('feedback', feedbackType);
};

export const getFeedbackSession = () => {
  return sessionStorage.getItem('feedback');
};

export const clearFeedbackSession = () => {
  sessionStorage.removeItem('feedback');
};
